import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Tooltip, useTheme } from '@material-ui/core';
import { IStudentAssignment } from '../../../../config/api/models/Assignment';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { Deadline, TaskTypeList, Icons } from '../../../shared/components';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../shared/hooks';
import apiRoutes from '../../../../config/api/routes';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import {
  IconButtonsWrapper,
  StyledIcon,
  StyledIconButton,
  SubjectWrapper,
  TrajectoryTitle,
  Wrapper,
} from './Atoms';
import { startOfDay, addDays, isBefore, parseISO } from 'date-fns';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';

const TODAY = startOfDay(new Date());
const TWO_DAYS_FROM_NOW = addDays(new Date(), 2);
const FIVE_DAYS_FROM_NOW = addDays(new Date(), 5);

export type Props = {
  assignment: IStudentAssignment;
  onFinishAssignment?: () => void;
  sidebarMode?: boolean;
};
const StudentAssignmentItem: FC<Props> = ({
  assignment,
  onFinishAssignment,
  sidebarMode = false,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const { getPath } = useI18nPath();
  const { t } = useTranslation();
  const [finishButtonEnabled, setFinishButtonEnabled] = useState(true);
  const log = useDataLogging();

  const [finishAssignmentResponse, finishAssignment] = useFetch('', {
    method: 'PATCH',
  });

  const handleContinueClick = useCallback(() => {
    history.push(
      getPath('trajectoryView', {
        bookId: assignment.bookId,
        chapterId: assignment.chapterId,
        trajectoryId: assignment.trajectoryId,
      })
    );
  }, [assignment.bookId, assignment.chapterId, getPath, history, assignment.trajectoryId]);

  const handleCompleteAssignment = () => {
    setFinishButtonEnabled(false);
    finishAssignment({
      path: apiRoutes.finishAssignment(assignment.id),
      body: {
        type: 'finish',
      },
    });
    log(LogActionTypes.STUDENT_FINISH_ASSIGNMENT, {
      assignmentId: assignment.id,
      assignmentStatus: 'finish',
    });
  };

  useEffect(() => {
    if (finishAssignmentResponse.success && onFinishAssignment) {
      onFinishAssignment();
    }
  }, [finishAssignmentResponse, onFinishAssignment]);

  const color = useMemo(() => {
    if (!!assignment.completedAt) return 'rgb(220,237,200)';
    if (!assignment.expiresAt) return 'rgba(0, 0, 0, 0.05)';

    if (isBefore(parseISO(assignment.expiresAt), TODAY))
      return theme.palette.urgentTransparent.main; //red
    if (isBefore(parseISO(assignment.expiresAt), TWO_DAYS_FROM_NOW)) return 'rgba(235,164,6,0.65)'; //orange
    if (isBefore(parseISO(assignment.expiresAt), FIVE_DAYS_FROM_NOW))
      return theme.palette.warningTransparent.main; //yellow

    return 'rgba(220, 237, 200, 1)'; //green
  }, [
    assignment.completedAt,
    assignment.expiresAt,
    theme.palette.urgentTransparent.main,
    theme.palette.warningTransparent.main,
  ]);
  return (
    <Wrapper color={color}>
      {<SubjectWrapper>{assignment.bookTitle}</SubjectWrapper>}

      <IconButtonsWrapper>
        {onFinishAssignment && !assignment.completedAt && (
          <Tooltip title={t('studentDashboard.assignments.completeAssignment')}>
            <StyledIconButton onClick={handleCompleteAssignment} disabled={!finishButtonEnabled}>
              <FontAwesomeSpriteIcon
                color={theme.palette.grey['700']}
                icon={SpriteIconsLight.CIRCLE_CHECK}
                iconSize="sm"
              />
            </StyledIconButton>
          </Tooltip>
        )}
        {!sidebarMode && (
          <Tooltip title={t('shared.toTrajectory')}>
            <StyledIconButton onClick={handleContinueClick} disabled={!finishButtonEnabled}>
              <FontAwesomeSpriteIcon
                color={theme.palette.grey['700']}
                icon={SpriteIconsLight.CIRCLE_ARROW_RIGHT}
                iconSize="sm"
              />
            </StyledIconButton>
          </Tooltip>
        )}
      </IconButtonsWrapper>

      <div>
        <TrajectoryTitle variant="h3">
          <StyledIcon icon={Icons.TRAJECTORY} />
          {assignment.trajectoryTitle}
        </TrajectoryTitle>
        {assignment.expiresAt && (
          <Deadline date={assignment.expiresAt} isCompleted={!!assignment.completedAt} />
        )}
      </div>

      <TaskTypeList taskTypes={assignment.taskTypes} series={assignment.series} />
    </Wrapper>
  );
};

export default StudentAssignmentItem;
